import {
  useRef,
  useState,
  useEffect,
  useCallback,
  CSSProperties,
  PointerEvent as ReactPointerEvent,
} from 'react';

import {
  useShadowRoot,
  TechDocsAddonLocations,
  createTechDocsAddonExtension,
} from '@backstage/plugin-techdocs-react';

import { createPortal } from 'react-dom';
import { createPlugin } from '@backstage/core-plugin-api';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

// This styles are needed to keep the addon self-contained and avoid shadow-DOM styling pitfalls.
const overlayStyle: CSSProperties = {
  inset: 0,
  zIndex: 2000,
  padding: '2rem',
  display: 'flex',
  position: 'fixed',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
};

const backdropStyle: CSSProperties = {
  inset: 0,
  margin: 0,
  padding: 0,
  width: '100%',
  height: '100%',
  border: 'none',
  cursor: 'zoom-out',
  position: 'absolute',
  background: 'transparent',
};

const imageStyle: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  position: 'relative',
  objectFit: 'contain',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
};

// Shared sx for the circular control IconButtons so they stay legible over the
// dark overlay while using the standard MUI button used elsewhere.
const controlButtonSx = {
  color: '#fff',
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.24)' },
} as const;

const closeButtonSx = {
  position: 'absolute',
  top: '1rem',
  right: '1rem',
  zIndex: 1,
  color: '#fff',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
} as const;

// The viewer overlay sits at zIndex 2000, so tooltips (default Popper zIndex
// 1500) must be lifted above it or they render behind the diagram.
const tooltipSlotProps = { popper: { sx: { zIndex: 2100 } } } as const;

// Inline SVG diagrams (e.g. PlantUML/Mermaid) are line-art on a transparent
// background, so they're placed on a white panel to stay visible over the dark
// overlay. The nested <style> forces the serialized <svg> to scale to fit.
const svgPanelStyle: CSSProperties = {
  width: '90vw',
  height: '85vh',
  display: 'flex',
  padding: '1rem',
  background: '#fff',
  overflow: 'hidden',
  borderRadius: '4px',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
};

const MAX_ZOOM = 8;
const MIN_ZOOM = 0.2;
const ZOOM_STEP = 1.25;
const MIN_SVG_ZOOM_SIZE = 48;

const controlsStyle: CSSProperties = {
  zIndex: 2,
  left: '50%',
  gap: '0.5rem',
  display: 'flex',
  bottom: '1.5rem',
  padding: '0.35rem',
  position: 'absolute',
  borderRadius: '999px',
  transform: 'translateX(-50%)',
  background: 'rgba(0, 0, 0, 0.55)',
};

type ZoomedContent =
  | { kind: 'svg'; markup: string }
  | { kind: 'image'; src: string; alt: string };

export const TechDocsImageZoomContent = () => {
  const shadowRoot = useShadowRoot();
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [zoomed, setZoomed] = useState<ZoomedContent | null>(null);
  const triggerRef = useRef<HTMLElement | SVGElement | null>(null);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);

  const close = useCallback(() => setZoomed(null), []);

  const zoomIn = useCallback(
    () => setScale(s => Math.min(s * ZOOM_STEP, MAX_ZOOM)),
    [],
  );

  const zoomOut = useCallback(
    () => setScale(s => Math.max(s / ZOOM_STEP, MIN_ZOOM)),
    [],
  );

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragOrigin.current = {
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    setOffset({
      x: event.clientX - dragOrigin.current.x,
      y: event.clientY - dragOrigin.current.y,
    });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragOrigin.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    if (!shadowRoot) return undefined;

    // Give content images and diagrams a zoom cursor. Injected on the stable
    // shadow root so it survives TechDocs re-rendering the article content.
    const style = document.createElement('style');
    style.textContent = '.md-content img, .md-content svg { cursor: zoom-in; }';
    shadowRoot.appendChild(style);

    // Delegate clicks on the shadow root itself. The root is stable across
    // content re-renders, unlike individual <img>/<svg> elements.
    const onClick = (event: Event) => {
      const target = event.target as Element | null;
      if (!target || !target.closest('.md-content')) return;

      if (target instanceof HTMLImageElement) {
        // Prevent navigation when the image is wrapped in a link.
        event.preventDefault();
        event.stopPropagation();
        triggerRef.current = target;
        setZoomed({
          kind: 'image',
          alt: target.alt,
          src: target.currentSrc || target.src,
        });
        return;
      }

      // Inline SVG diagrams: the click target is usually a child node
      // (path/text), so resolve up to the containing <svg>.
      const svg = target.closest('svg') as SVGSVGElement | null;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      if (rect.width < MIN_SVG_ZOOM_SIZE && rect.height < MIN_SVG_ZOOM_SIZE) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      triggerRef.current = svg;
      // Clone so the live diagram is untouched, and center the drawing within
      // its box (PlantUML defaults to xMaxYMax, pinning it to a corner).
      const clone = svg.cloneNode(true) as SVGSVGElement;
      clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      setZoomed({ kind: 'svg', markup: clone.outerHTML });
    };
    shadowRoot.addEventListener('click', onClick, true);

    return () => {
      shadowRoot.removeEventListener('click', onClick, true);
      style.remove();
    };
  }, [shadowRoot]);

  useEffect(() => {
    if (!zoomed) return undefined;

    // Start each viewing session at the default zoom/pan.
    setScale(1);
    setOffset({ x: 0, y: 0 });

    // Move focus into the dialog and restore it to the trigger on close.
    const trigger = triggerRef.current;
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, [zoomed, close]);

  if (!zoomed) {
    return null;
  }

  const stageStyle: CSSProperties = {
    zIndex: 1,
    display: 'flex',
    maxWidth: '100%',
    maxHeight: '100%',
    touchAction: 'none',
    position: 'relative',
    transformOrigin: 'center center',
    cursor: dragging ? 'grabbing' : 'grab',
    transition: dragging ? 'none' : 'transform 0.1s ease-out',
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      style={overlayStyle}
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={close}
        style={backdropStyle}
      />
      <Tooltip title="Close" slotProps={tooltipSlotProps}>
        <IconButton
          ref={closeButtonRef}
          aria-label="Close image viewer"
          onClick={close}
          sx={closeButtonSx}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
      <div
        style={stageStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {zoomed.kind === 'image' ? (
          <img
            src={zoomed.src}
            alt={zoomed.alt}
            draggable={false}
            style={imageStyle}
          />
        ) : (
          <div style={svgPanelStyle}>
            <style>
              {
                '.techdocs-zoom-svg { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; } .techdocs-zoom-svg svg { width: 100%; height: 100%; }'
              }
            </style>
            {/* Markup originates from TechDocs-rendered (sanitized) content and
                is re-inserted unchanged for full-size display. */}
            <div
              className="techdocs-zoom-svg"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: zoomed.markup }}
            />
          </div>
        )}
      </div>
      <div style={controlsStyle}>
        <Tooltip title="Zoom out" slotProps={tooltipSlotProps}>
          <IconButton
            aria-label="Zoom out"
            onClick={zoomOut}
            sx={controlButtonSx}
          >
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset zoom" slotProps={tooltipSlotProps}>
          <IconButton
            aria-label="Reset zoom"
            onClick={resetView}
            sx={controlButtonSx}
          >
            <CenterFocusStrongIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in" slotProps={tooltipSlotProps}>
          <IconButton
            aria-label="Zoom in"
            onClick={zoomIn}
            sx={controlButtonSx}
          >
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>,
    document.body,
  );
};

const techDocsImageZoomPlugin = createPlugin({
  id: 'techDocsImageZoom',
});

export const TechDocsImageZoom = techDocsImageZoomPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsImageZoom',
    location: TechDocsAddonLocations.Content,
    component: TechDocsImageZoomContent,
  }),
);
